import Button from 'tio-reimagined/components/button';
import { eq, or, not, and } from 'tio-ui/utilities';
import { hash, fn } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import type RouterService from '@ember/routing/router-service';
import Modal from 'tio-reimagined/components/modal';
import { on } from '@ember/modifier';
import ReSelect from 'tio-reimagined/components/select';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { type IntlService } from 'ember-intl';

import Component from '@glimmer/component';
import Drawer from 'tio-reimagined/components/drawer';
import FileUploader from 'tio-ui/components/file-uploader';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import type { UploadFile, Queue } from 'ember-file-upload';
import type FinancialInstitutionModel from 'tio-data/models/financial-institution';
import type ObservabilityService from 'tio-employee/services/observability';
import type ObservableDocumentModel from 'tio-data/models/observable-document';
import type ObservablesService from 'tio-common/services/observables';
import { registerDestructor } from '@ember/destroyable';
import type RemoteEventLoggerService from 'tio-employee/services/remote-event-logger';
import UploadProgress from 'tio-employee/components/observability/upload-progress';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type { ObservableSource } from 'tio-employee/services/observability';
import ObservabilityUploadController from 'tio-employee/controllers/authenticated/observability/upload';
import ReimaginedDocumentCenter from 'tio-employee/components/reimagined/production/document-center';
import type { ReimaginedDocumentCenterModel } from 'tio-employee/components/reimagined/production/document-center';
import type ReimaginedViewService from 'tio-employee/services/reimagined-view';
import type { ObservabilityUploadRouteModel } from 'tio-employee/routes/authenticated/observability/upload';
import {
  getObservableProductRoute,
  getInstitutionMatch,
} from 'tio-employee/services/observability';
import type { ProviderParam } from 'tio-employee/services/observability';
import { Header, Section, VStack, HStack } from 'tio-reimagined/components/layout';
import ActionableTranslation from 'tio-ui/components/actionable-translation';
import type Owner from '@ember/owner';
import {
  PhFile,
  PhXCircle,
  PhCaretLeft,
  PhCloudArrowUp,
  PhArrowRight,
  PhArrowLeft,
  PhGlobeSimple,
  PhFileText,
  PhShieldCheck,
  PhLightning,
  PhChartLineUp,
  PhInfo,
  PhWarning,
  PhCheckCircle,
} from 'ember-phosphor-icons';
import WizardStepIndicator from 'tio-employee/components/reimagined/tas/wizard-step-indicator';
import type { WizardStep } from 'tio-employee/components/reimagined/tas/wizard-step-indicator';
import PageHero from 'tio-reimagined/components/page-hero';
import SectionHeader from 'tio-reimagined/components/section-header';

// Accepted file types for document upload
const ACCEPTED_FILE_TYPES = '.pdf,.png,.jpg,.jpeg,.txt';
const NSLDS_ACCEPTED_FILE_TYPES = '.txt';
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

// wait 10s before telling users to wait for an email
const PROCESSING_TIMEOUT = 10000;
const DEFAULT_PREFERRED_PROVIDER = 'statement';

type WizardStepId = 'choose' | 'instructions' | 'upload' | 'complete';

const WIZARD_STEP_IDS: WizardStepId[] = ['choose', 'instructions', 'upload', 'complete'];

const VALID_EXTENSIONS = ['pdf', 'png', 'jpg', 'jpeg', 'txt'];
const NSLDS_VALID_EXTENSIONS = ['txt'];

const getFileExtension = (filename: string): string => {
  return filename.split('.').pop()?.toLowerCase() ?? '';
};

const isValidFileSize = (file: File): boolean => {
  return file.size <= MAX_FILE_SIZE_BYTES;
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const providerCtaTranslation = (provider: ProviderParam) => {
  switch (provider) {
    case 'statement':
      return 'observability.uploads.statement.cta';
    case 'nslds':
      return 'observability.uploads.nslds.cta';
    case 'transaction_history':
      return 'observability.uploads.transaction_history.cta';
  }
};

const providerPromptTranslation = (provider: ProviderParam) => {
  switch (provider) {
    case 'statement':
      return 'observability.uploads.statement.prompt';
    case 'nslds':
      return 'observability.uploads.nslds.prompt';
    case 'transaction_history':
      return 'observability.uploads.transaction_history.prompt';
  }
};

const documentTypeNameTranslation = (provider?: ProviderParam) => {
  switch (provider) {
    case 'statement':
      return 'observability.statement';
    case 'nslds':
      return 'observability.nslds';
    case 'transaction_history':
      return 'observability.transaction_history';
    default:
      return 'observability.default_document';
  }
};

const drawerHeaderTranslation = (provider?: ProviderParam) => {
  switch (provider) {
    case 'statement':
      return 'observability.uploads.statement.prompt';
    case 'nslds':
      return 'observability.uploads.nslds.prompt';
    case 'transaction_history':
      return 'observability.uploads.transaction_history.prompt';
    default:
      return 'observability.uploads.statement.prompt';
  }
};

const drawerInstructionsTranslation = (provider?: ProviderParam) => {
  switch (provider) {
    case 'statement':
      return 'observability.uploads.statement.instructions';
    case 'nslds':
      return 'observability.uploads.nslds.instructions';
    case 'transaction_history':
      return 'observability.uploads.transaction_history.instructions';
    default:
      return 'observability.uploads.statement.instructions';
  }
};

interface ObservabilityUploadRouteSignature {
  Args: {
    controller: ObservabilityUploadController;
    model: ObservabilityUploadRouteModel;
  };
}

export default class ObservabilityUploadRoute extends Component<ObservabilityUploadRouteSignature> {
  @service declare router: RouterService;
  @service declare observability: ObservabilityService;
  @service declare observables: ObservablesService;
  @service declare fileQueue: Queue;
  @service declare intl: IntlService;
  @service declare remoteEventLogger: RemoteEventLoggerService;
  @service declare reimaginedData: ReimaginedDataService;
  @service declare reimaginedView: ReimaginedViewService;

  @tracked batchId?: string;
  @tracked instructionsOpen: boolean = false;
  @tracked instructionsType?: 'nslds' | 'statement' | 'transaction_history';
  // this can be an incomplete mock financial institution in order to hit the translation
  // select clause default case when the name doesn't match; see servicerOptions getter's
  // return value with mock servicer "General Instructions" spread into it
  @tracked financialInstitution?: FinancialInstitutionModel | { name: string };
  @tracked upload?: UploadFile;
  @tracked observableDocument?: ObservableDocumentModel;
  subscription:
    | ReturnType<typeof this.observability.cable.consumer>['subscriptions']['create']
    | null = null;
  @tracked uploading = false;
  @tracked processing = false;

  noop = () => {};
  @tracked uploadError?: Error;
  @tracked uploadTimeout?: ReturnType<typeof setTimeout>;
  @tracked timedOut = false;

  // Wizard state (reimagined flow)
  @tracked wizardStep: WizardStepId = 'choose';
  @tracked selectedDocType: ProviderParam | null = null;
  @tracked fileValidationError: string | null = null;

  constructor(owner: Owner, args: ObservabilityUploadRouteSignature['Args']) {
    super(owner, args);
    this.batchId = crypto.randomUUID();

    if (this.args.controller.source === 'wellness') {
      this.remoteEventLogger.logObservabilityEvent({
        eventName: 'INITIALIZE',
        component: 'ObservabilityUploadRoute',
      });
    }

    // cleanup upload timeout on unmount
    registerDestructor(this, () => {
      clearTimeout(this.uploadTimeout);
      this.observability.unsubscribeFromBatchStatus();
    });
  }

  /***************************/
  /* Accessors               */
  /***************************/

  get providers(): ProviderParam[] {
    const matches = this.args.controller.providers
      .split(',')
      .filter(Boolean)
      .map((p) => p.toLowerCase());
    return <ProviderParam[]>matches;
  }

  get preferredProvider(): ProviderParam {
    const match = this.args.controller.providers.split(',').filter(Boolean).at(0)?.toLowerCase();
    return <ProviderParam>match || DEFAULT_PREFERRED_PROVIDER;
  }

  get alternateProvider(): ProviderParam | undefined {
    const match = this.args.controller.providers.split(',').filter(Boolean).at(1)?.toLowerCase();
    if (match) return <ProviderParam>match;
  }

  get productSource(): ObservableSource {
    return <ObservableSource>this.args.controller.source;
  }

  get useReimaginedDocuments() {
    return this.reimaginedView.isEnabled;
  }

  get reimaginedModel(): ReimaginedDocumentCenterModel | undefined {
    if (this.useReimaginedDocuments && 'reimagined' in this.args.model) {
      return (this.args.model as { reimagined: ReimaginedDocumentCenterModel }).reimagined;
    }
    return undefined;
  }

  get isReimaginedEntry() {
    const { source, returnTo } = this.args.controller;
    return source?.startsWith('reimagined') || returnTo?.startsWith('authenticated.reimagined');
  }

  get hasUploadIntent() {
    const { source, providers } = this.args.controller;
    return !!(source || providers);
  }

  get backRoute() {
    if (this.returnTo) {
      return this.returnTo;
    }
    return getObservableProductRoute(this.productSource);
  }

  get returnTo(): string {
    return this.args.controller.returnTo;
  }

  get servicerOptions() {
    const placeholderLabel = this.intl.t('observability.uploads.placeholder_servicer');
    return [
      { key: 'general', label: placeholderLabel },
      ...this.args.model.financialInstitutions.map((fi) => ({ key: fi.name, label: fi.name })),
    ];
  }

  get needsServicer() {
    return ['statement', 'transaction_history'].includes(<string>this.instructionsType);
  }

  get selectedServicerKey(): string | undefined {
    if (!this.financialInstitution) return undefined;
    // 'general' is the key for the placeholder/default instructions
    return this.financialInstitution.name === 'default'
      ? 'general'
      : this.financialInstitution.name;
  }

  get queue() {
    // @ts-expect-error "Property 'findOrCreate' does not existon type 'Queue'" - yes it does
    return this.fileQueue.findOrCreate('observable-assets');
  }

  /***************************/
  /* Actions                 */
  /***************************/

  openInstructions = (provider: ProviderParam) => {
    this.instructionsType = provider;
    this.instructionsOpen = true;
  };

  closeInstructions = () => {
    this.instructionsOpen = false;
    this.instructionsType = undefined;
    this.financialInstitution = undefined;
  };

  setFinancialInstitution = (key: string) => {
    if (key === 'general') {
      // Use mock institution to hit the default translation case
      this.financialInstitution = { name: 'default' };
    } else {
      this.financialInstitution = this.args.model.financialInstitutions.find(
        (institution: FinancialInstitutionModel) => institution.name === key
      ) || { name: 'default' };
    }
  };

  handleFileAdded = (upload: UploadFile) => {
    this.upload = upload;
  };

  removeFile = () => {
    this.queue.remove(this.upload);
    this.upload = undefined;
  };

  handleSubmit = async (e: Event) => {
    e.preventDefault();
    const { person } = this.args.model;

    this.uploading = true;
    this.subscription = this.observability.subscribeToBatchStatus(
      this.batchId!,
      this.productSource
    );

    this.remoteEventLogger.logObservabilityEvent({
      eventName: 'UPLOAD_START',
      component: 'ObservabilityUploadRoute',
    });

    try {
      const document = await this.observables.createObservableDocument(this.upload!, {
        person,
        batch: this.batchId,
      });

      this.remoteEventLogger.logObservabilityEvent({
        eventName: 'UPLOAD_SUCCESS',
        component: 'ObservabilityUploadRoute',
        document,
      });
      this.observableDocument = document;
      this.uploadTimeout = setTimeout(() => (this.timedOut = true), PROCESSING_TIMEOUT);
      this.processing = true;
      this.uploading = false;
    } catch (error) {
      this.remoteEventLogger.logObservabilityEvent({
        eventName: 'UPLOAD_FAILURE',
        component: 'ObservabilityUploadRoute',
      });
      this.uploadError = error as Error;
    }
  };

  reset = () => {
    clearTimeout(this.uploadTimeout);
    this.uploading = false;
    this.processing = false;
    this.queue.remove(this.upload);
    this.upload = undefined;
    this.uploadError = undefined;
  };

  /***************************/
  /* Wizard (Reimagined)     */
  /***************************/

  get wizardSteps(): WizardStep[] {
    const stepIndex = WIZARD_STEP_IDS.indexOf(this.wizardStep);
    const labels = [
      this.intl.t('observability.wizard.steps.choose'),
      this.intl.t('observability.wizard.steps.instructions'),
      this.intl.t('observability.wizard.steps.upload'),
      this.intl.t('observability.wizard.steps.done'),
    ];
    return labels.map((label, i) => ({
      number: i + 1,
      label,
      completed: i < stepIndex,
      active: i === stepIndex,
    }));
  }

  get docTypeLabel() {
    if (this.selectedDocType === 'nslds') return this.intl.t('observability.wizard.doc_type.nslds');
    if (this.selectedDocType === 'statement')
      return this.intl.t('observability.wizard.doc_type.statement');
    return '';
  }

  get hasNsldsOption() {
    return this.providers.includes('nslds');
  }

  get hasStatementOption() {
    return this.providers.includes('statement');
  }

  get acceptedFileTypes() {
    return this.selectedDocType === 'nslds' ? NSLDS_ACCEPTED_FILE_TYPES : ACCEPTED_FILE_TYPES;
  }

  get validExtensions() {
    return this.selectedDocType === 'nslds' ? NSLDS_VALID_EXTENSIONS : VALID_EXTENSIONS;
  }

  get isNsldsSelected() {
    return this.selectedDocType === 'nslds';
  }

  selectDocType = (docType: ProviderParam) => {
    this.selectedDocType = docType;
    this.wizardStep = 'instructions';
  };

  goToUploadStep = () => {
    this.wizardStep = 'upload';
  };

  goBackToChoose = () => {
    this.wizardStep = 'choose';
    this.selectedDocType = null;
  };

  goBackToInstructions = () => {
    this.wizardStep = 'instructions';
  };

  handleFileAddedWithValidation = (uploadFile: UploadFile) => {
    this.fileValidationError = null;
    const file = uploadFile.file;

    const allowedExtensions = this.validExtensions;
    const ext = getFileExtension(file.name);
    if (!allowedExtensions.includes(ext)) {
      this.fileValidationError =
        this.selectedDocType === 'nslds'
          ? this.intl.t('observability.wizard.upload.invalid_file_type_nslds')
          : this.intl.t('observability.wizard.upload.invalid_file_type');
      this.queue.remove(uploadFile);
      return;
    }

    if (!isValidFileSize(file)) {
      this.fileValidationError = this.intl.t('observability.wizard.upload.file_too_large', {
        fileSize: formatFileSize(file.size),
        maxSize: MAX_FILE_SIZE_MB,
      });
      this.queue.remove(uploadFile);
      return;
    }

    this.upload = uploadFile;
  };

  removeFileAndReset = () => {
    this.queue.remove(this.upload);
    this.upload = undefined;
    this.fileValidationError = null;
  };

  handleWizardSubmit = async (e: Event) => {
    e.preventDefault();
    if (!this.upload) return;

    const { person } = this.args.model;

    this.uploading = true;
    this.subscription = this.observability.subscribeToBatchStatus(
      this.batchId!,
      this.productSource
    );

    this.remoteEventLogger.logObservabilityEvent({
      eventName: 'UPLOAD_START',
      component: 'ObservabilityUploadRoute',
    });

    try {
      const document = await this.observables.createObservableDocument(this.upload!, {
        person,
        batch: this.batchId,
      });

      this.remoteEventLogger.logObservabilityEvent({
        eventName: 'UPLOAD_SUCCESS',
        component: 'ObservabilityUploadRoute',
        document,
      });
      this.observableDocument = document;
      this.uploadTimeout = setTimeout(() => (this.timedOut = true), PROCESSING_TIMEOUT);
      this.processing = false;
      this.uploading = false;
      this.wizardStep = 'complete';
    } catch (error) {
      this.remoteEventLogger.logObservabilityEvent({
        eventName: 'UPLOAD_FAILURE',
        component: 'ObservabilityUploadRoute',
      });
      this.uploadError = error as Error;
      this.uploading = false;
    }
  };

  resetWizard = () => {
    clearTimeout(this.uploadTimeout);
    this.uploading = false;
    this.processing = false;
    this.queue.remove(this.upload);
    this.upload = undefined;
    this.uploadError = undefined;
    this.fileValidationError = null;
    this.wizardStep = 'choose';
    this.selectedDocType = null;
    this.timedOut = false;
    this.batchId = crypto.randomUUID();
  };

  <template>
    {{#if (and this.useReimaginedDocuments (not this.hasUploadIntent))}}
      {{#if this.reimaginedModel}}
        <ReimaginedDocumentCenter @model={{this.reimaginedModel}} />
      {{/if}}
    {{else if (or this.isReimaginedEntry this.useReimaginedDocuments)}}
      {{! template-lint-disable no-bare-strings }}
      <VStack class="gap-6 pb-12">
        {{! Back Navigation }}
        <div>
          <LinkTo
            @route={{this.backRoute}}
            @query={{hash source="" providers="" returnTo=""}}
            class="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
          >
            <PhArrowLeft @size={{16}} />
            {{t "observability.wizard.back"}}
          </LinkTo>
        </div>

        {{! Hero }}
        <PageHero
          @icon={{PhCloudArrowUp}}
          @title="Upload Document"
          @subtitle="Share your financial documents to unlock employer contributions and track your repayment progress."
          @theme="emerald"
          @decoration="circles"
          @rounded="2xl"
        />

        {{! Step indicator }}
        <WizardStepIndicator @steps={{this.wizardSteps}} />

        {{! ═══════════════════════════════════ }}
        {{! STEP 1: CHOOSE DOCUMENT TYPE        }}
        {{! ═══════════════════════════════════ }}
        {{#if (eq this.wizardStep "choose")}}
          <div class="max-w-3xl mx-auto w-full space-y-6">
            <SectionHeader
              @icon={{PhCloudArrowUp}}
              @title={{t "observability.wizard.choose.heading"}}
              @subtitle={{t "observability.wizard.choose.description"}}
              @iconClass="text-emerald-500"
            />

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {{#if this.hasNsldsOption}}
                <button
                  type="button"
                  class="group relative bg-white rounded-xl ring-1 ring-gray-950/4 shadow-sm p-6 text-left transition-all hover:ring-emerald-400 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  {{on "click" (fn this.selectDocType "nslds")}}
                >
                  <div class="absolute top-3 right-3">
                    <span
                      class="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700"
                    >
                      <PhLightning @size={{12}} />
                      {{t "observability.wizard.choose.recommended"}}
                    </span>
                  </div>
                  <div
                    class="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-100 mb-4"
                  >
                    <PhGlobeSimple class="h-6 w-6 text-emerald-600" />
                  </div>
                  <Heading @level={{3}} class="text-lg font-semibold text-gray-900 mb-1">
                    {{t "observability.wizard.choose.nslds_title"}}
                  </Heading>
                  <Text class="text-sm text-gray-600 mb-3">
                    {{t "observability.wizard.choose.nslds_description"}}
                  </Text>
                  <div
                    class="flex items-center gap-2 text-emerald-600 text-sm font-medium group-hover:gap-3 transition-all"
                  >
                    {{t "observability.wizard.choose.choose_this"}}
                    <PhArrowRight @size={{14}} />
                  </div>
                </button>
              {{/if}}

              {{#if this.hasStatementOption}}
                <button
                  type="button"
                  class="group bg-white rounded-xl ring-1 ring-gray-950/4 shadow-sm p-6 text-left transition-all hover:ring-indigo-400 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  {{on "click" (fn this.selectDocType "statement")}}
                >
                  <div
                    class="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-100 mb-4"
                  >
                    <PhFileText class="h-6 w-6 text-indigo-600" />
                  </div>
                  <Heading @level={{3}} class="text-lg font-semibold text-gray-900 mb-1">
                    {{t "observability.wizard.choose.statement_title"}}
                  </Heading>
                  <Text class="text-sm text-gray-600 mb-3">
                    {{t "observability.wizard.choose.statement_description"}}
                  </Text>
                  <div
                    class="flex items-center gap-2 text-indigo-600 text-sm font-medium group-hover:gap-3 transition-all"
                  >
                    {{t "observability.wizard.choose.choose_this"}}
                    <PhArrowRight @size={{14}} />
                  </div>
                </button>
              {{/if}}
            </div>

            {{! What this unlocks }}
            <div class="bg-white rounded-xl p-6 shadow-sm ring-1 ring-gray-950/4">
              <Heading
                @level={{3}}
                class="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4"
              >
                {{t "observability.wizard.choose.why_upload"}}
              </Heading>
              <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div class="flex items-start gap-3">
                  <PhShieldCheck class="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
                  <Text class="text-sm text-gray-700">{{t
                      "observability.wizard.choose.benefit_contributions"
                    }}</Text>
                </div>
                <div class="flex items-start gap-3">
                  <PhChartLineUp class="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
                  <Text class="text-sm text-gray-700">{{t
                      "observability.wizard.choose.benefit_tracking"
                    }}</Text>
                </div>
                <div class="flex items-start gap-3">
                  <PhLightning class="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
                  <Text class="text-sm text-gray-700">{{t
                      "observability.wizard.choose.benefit_forgiveness"
                    }}</Text>
                </div>
              </div>
            </div>
          </div>

          {{! ═══════════════════════════════════ }}
          {{! STEP 2: INLINE INSTRUCTIONS         }}
          {{! ═══════════════════════════════════ }}
        {{else if (eq this.wizardStep "instructions")}}
          <div class="max-w-3xl mx-auto w-full">
            <div class="bg-white rounded-xl shadow-sm ring-1 ring-gray-950/4 overflow-hidden">
              {{! Header }}
              <div
                class="bg-linear-to-r from-emerald-50 to-teal-50 px-6 py-5 border-b border-gray-100"
              >
                <div class="flex items-center gap-3">
                  {{#if (eq this.selectedDocType "nslds")}}
                    <div
                      class="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100"
                    >
                      <PhGlobeSimple class="h-5 w-5 text-emerald-600" />
                    </div>
                  {{else}}
                    <div
                      class="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-100"
                    >
                      <PhFileText class="h-5 w-5 text-indigo-600" />
                    </div>
                  {{/if}}
                  <div>
                    <Heading @level={{2}} class="text-lg font-semibold text-gray-900">
                      {{t "observability.wizard.instructions.heading" docType=this.docTypeLabel}}
                    </Heading>
                    <Text class="text-sm text-gray-500">{{t
                        "observability.wizard.instructions.follow_steps"
                      }}</Text>
                  </div>
                </div>
              </div>

              {{! Instructions body }}
              <div class="p-6">
                {{#if (eq this.selectedDocType "nslds")}}
                  <ol class="space-y-4 mb-6">
                    <li class="flex gap-4">
                      <span
                        class="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 text-sm font-bold shrink-0"
                      >{{1}}</span>
                      <div>
                        <Text class="font-medium text-gray-900">{{t
                            "observability.wizard.instructions.nslds.step1_title"
                          }}</Text>
                        <Text class="text-sm text-gray-500 mt-0.5">
                          <a
                            href="https://studentaid.gov/aid-summary"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="text-emerald-600 underline hover:text-emerald-700"
                          >
                            {{t "observability.wizard.instructions.nslds.step1_link_text"}}
                          </a>
                        </Text>
                      </div>
                    </li>
                    <li class="flex gap-4">
                      <span
                        class="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 text-sm font-bold shrink-0"
                      >{{2}}</span>
                      <div>
                        <Text class="font-medium text-gray-900">{{t
                            "observability.wizard.instructions.nslds.step2_title"
                          }}</Text>
                        <Text class="text-sm text-gray-500 mt-0.5">{{t
                            "observability.wizard.instructions.nslds.step2_description"
                          }}</Text>
                      </div>
                    </li>
                    <li class="flex gap-4">
                      <span
                        class="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 text-sm font-bold shrink-0"
                      >{{3}}</span>
                      <div>
                        <Text class="font-medium text-gray-900">{{t
                            "observability.wizard.instructions.nslds.step3_title"
                          }}</Text>
                        <Text class="text-sm text-gray-500 mt-0.5">{{t
                            "observability.wizard.instructions.nslds.step3_description"
                          }}</Text>
                      </div>
                    </li>
                  </ol>

                  <div class="bg-emerald-50 rounded-xl p-4 flex items-start gap-3 mb-6">
                    <PhInfo class="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                    <Text class="text-sm text-emerald-800">
                      <span class="font-medium">{{t
                          "observability.wizard.instructions.nslds.why_nslds"
                        }}</span>
                      {{t "observability.wizard.instructions.nslds.why_nslds_body"}}
                    </Text>
                  </div>
                {{else}}
                  <ol class="space-y-4 mb-6">
                    <li class="flex gap-4">
                      <span
                        class="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 text-indigo-700 text-sm font-bold shrink-0"
                      >{{1}}</span>
                      <div>
                        <Text class="font-medium text-gray-900">{{t
                            "observability.wizard.instructions.statement.step1_title"
                          }}</Text>
                        <Text class="text-sm text-gray-500 mt-0.5">{{t
                            "observability.wizard.instructions.statement.step1_description"
                          }}</Text>
                      </div>
                    </li>
                    <li class="flex gap-4">
                      <span
                        class="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 text-indigo-700 text-sm font-bold shrink-0"
                      >{{2}}</span>
                      <div>
                        <Text class="font-medium text-gray-900">{{t
                            "observability.wizard.instructions.statement.step2_title"
                          }}</Text>
                        <Text class="text-sm text-gray-500 mt-0.5">{{t
                            "observability.wizard.instructions.statement.step2_description"
                          }}</Text>
                      </div>
                    </li>
                    <li class="flex gap-4">
                      <span
                        class="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 text-indigo-700 text-sm font-bold shrink-0"
                      >{{3}}</span>
                      <div>
                        <Text class="font-medium text-gray-900">{{t
                            "observability.wizard.instructions.statement.step3_title"
                          }}</Text>
                        <Text class="text-sm text-gray-500 mt-0.5">{{t
                            "observability.wizard.instructions.statement.step3_description"
                          }}</Text>
                      </div>
                    </li>
                  </ol>

                  <div class="bg-indigo-50 rounded-xl p-4 flex items-start gap-3 mb-6">
                    <PhInfo class="h-5 w-5 text-indigo-600 shrink-0 mt-0.5" />
                    <Text class="text-sm text-indigo-800">
                      <span class="font-medium">{{t
                          "observability.wizard.instructions.statement.what_we_need"
                        }}</span>
                      {{t "observability.wizard.instructions.statement.what_we_need_body"}}
                    </Text>
                  </div>
                {{/if}}

                <div class="bg-gray-50 rounded-xl p-4 mb-6">
                  <Text class="text-sm font-medium text-gray-700 mb-2">{{t
                      "observability.wizard.instructions.accepted_file_types"
                    }}</Text>
                  {{! template-lint-disable no-bare-strings }}
                  <div class="flex flex-wrap gap-2">
                    {{#if this.isNsldsSelected}}
                      <span
                        class="inline-flex items-center rounded-lg bg-white border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700"
                      >TXT</span>
                    {{else}}
                      <span
                        class="inline-flex items-center rounded-lg bg-white border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700"
                      >PDF</span>
                      <span
                        class="inline-flex items-center rounded-lg bg-white border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700"
                      >TXT</span>
                      <span
                        class="inline-flex items-center rounded-lg bg-white border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700"
                      >PNG</span>
                      <span
                        class="inline-flex items-center rounded-lg bg-white border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700"
                      >JPG</span>
                    {{/if}}
                    <span class="text-xs text-gray-400 self-center ml-1">Max
                      {{MAX_FILE_SIZE_MB}}MB</span>
                  </div>
                  {{! template-lint-enable no-bare-strings }}
                </div>

                {{! Navigation }}
                <div class="flex items-center justify-between pt-2">
                  <button
                    type="button"
                    class="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 font-medium"
                    {{on "click" this.goBackToChoose}}
                  >
                    <PhArrowLeft @size={{14}} />
                    {{t "observability.wizard.instructions.change_doc_type"}}
                  </button>
                  <Button
                    @intent="primary"
                    class="*:justify-center"
                    {{on "click" this.goToUploadStep}}
                  >
                    <span class="flex items-center gap-2">
                      {{t "observability.wizard.instructions.have_my_file"}}
                      <PhArrowRight @size={{16}} />
                    </span>
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {{! ═══════════════════════════════════ }}
          {{! STEP 3: UPLOAD FILE                 }}
          {{! ═══════════════════════════════════ }}
        {{else if (eq this.wizardStep "upload")}}
          <div class="max-w-3xl mx-auto w-full">
            <div class="bg-white rounded-xl shadow-sm ring-1 ring-gray-950/4 p-6">
              <div class="text-center mb-6">
                <Heading @level={{2}} class="text-xl font-semibold text-gray-900 mb-1">
                  {{t "observability.wizard.upload.heading" docType=this.docTypeLabel}}
                </Heading>
                <Text class="text-sm text-gray-500">
                  {{t "observability.wizard.upload.drag_drop"}}
                </Text>
              </div>

              <form {{on "submit" this.handleWizardSubmit}}>
                {{#if this.upload}}
                  {{! File selected state }}
                  <div class="rounded-xl border-2 border-emerald-200 bg-emerald-50 p-4 sm:p-6 mb-4">
                    <div class="flex items-center gap-3">
                      <div
                        class="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 shrink-0"
                      >
                        <PhFile class="h-5 w-5 text-emerald-600" />
                      </div>
                      <div class="flex-1 min-w-0 overflow-hidden">
                        <p
                          class="font-medium text-gray-900 truncate text-sm"
                        >{{this.upload.file.name}}</p>
                        <Text class="text-sm text-emerald-600">{{t
                            "observability.wizard.upload.ready"
                          }}</Text>
                      </div>
                      <button
                        type="button"
                        class="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors shrink-0"
                        {{on "click" this.removeFileAndReset}}
                      >
                        <PhXCircle @size={{20}} />
                      </button>
                    </div>
                  </div>
                {{else}}
                  {{! Drop zone }}
                  <FileUploader
                    @name="observable-assets"
                    @accept={{this.acceptedFileTypes}}
                    @onFileAdded={{this.handleFileAddedWithValidation}}
                    @useDropzone={{true}}
                  >
                    <button type="submit" class="w-full">
                      <div
                        class="rounded-xl border-2 border-dashed border-gray-300 hover:border-emerald-400 bg-gray-50 hover:bg-emerald-50 p-10 transition-all cursor-pointer text-center"
                      >
                        <PhCloudArrowUp class="h-10 w-10 text-gray-400 mx-auto mb-3" />
                        <Text class="font-medium text-gray-700 mb-1">
                          {{t "observability.wizard.upload.drop_or_browse"}}
                        </Text>
                        <Text class="text-sm text-gray-400">
                          {{#if this.isNsldsSelected}}
                            {{t
                              "observability.wizard.upload.file_types_hint_nslds"
                              size=MAX_FILE_SIZE_MB
                            }}
                          {{else}}
                            {{t
                              "observability.wizard.upload.file_types_hint"
                              size=MAX_FILE_SIZE_MB
                            }}
                          {{/if}}
                        </Text>
                      </div>
                    </button>
                  </FileUploader>
                {{/if}}

                {{! Validation error }}
                {{#if this.fileValidationError}}
                  <div
                    class="rounded-xl border border-red-200 bg-red-50 p-4 mt-4 flex items-start gap-3"
                  >
                    <PhWarning class="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
                    <div>
                      <Text
                        class="text-sm font-medium text-red-800"
                      >{{this.fileValidationError}}</Text>
                      <Text class="text-sm text-red-600 mt-1">
                        {{t "observability.wizard.upload.accepted_formats" size=MAX_FILE_SIZE_MB}}
                      </Text>
                    </div>
                  </div>
                {{/if}}

                {{! Upload error }}
                {{#if this.uploadError}}
                  <div
                    class="rounded-xl border border-red-200 bg-red-50 p-4 mt-4 flex items-start gap-3"
                  >
                    <PhWarning class="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
                    <div>
                      <Text class="text-sm font-medium text-red-800">{{t
                          "observability.wizard.upload.error_failed"
                        }}</Text>
                      <Text class="text-sm text-red-600 mt-1">
                        {{t "observability.wizard.upload.error_help"}}
                      </Text>
                    </div>
                  </div>
                {{/if}}

                {{! Actions }}
                <div class="flex items-center justify-between mt-6 pt-4 border-t border-gray-100">
                  <button
                    type="button"
                    class="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 font-medium"
                    {{on "click" this.goBackToInstructions}}
                  >
                    <PhArrowLeft @size={{14}} />
                    {{t "observability.wizard.upload.back_to_instructions"}}
                  </button>
                  <Button
                    @intent="primary"
                    type="submit"
                    @isDisabled={{or (not this.upload) this.uploading}}
                    @isRunning={{this.uploading}}
                    class="*:justify-center min-w-35"
                  >
                    {{#if this.uploading}}
                      {{t "observability.wizard.upload.uploading"}}
                    {{else}}
                      <span class="flex items-center gap-2">
                        {{t "observability.wizard.upload.submit"}}
                        <PhArrowRight @size={{16}} />
                      </span>
                    {{/if}}
                  </Button>
                </div>
              </form>
            </div>
          </div>

          {{! ═══════════════════════════════════ }}
          {{! STEP 4: SUCCESS / WHAT'S NEXT       }}
          {{! ═══════════════════════════════════ }}
        {{else if (eq this.wizardStep "complete")}}
          <div class="max-w-3xl mx-auto w-full">
            <div class="bg-white rounded-xl shadow-sm ring-1 ring-gray-950/4 overflow-hidden">
              {{! Success header }}
              <div class="bg-linear-to-r from-emerald-500 to-teal-500 px-6 py-8 text-center">
                <div
                  class="flex h-16 w-16 items-center justify-center rounded-full bg-white/20 mx-auto mb-4"
                >
                  <PhCheckCircle class="h-8 w-8 text-white" />
                </div>
                <Heading @level={{2}} class="text-white text-2xl font-bold mb-2">
                  {{t "observability.wizard.complete.heading"}}
                </Heading>
                <Text class="text-white/90">
                  {{t "observability.wizard.complete.description" docType=this.docTypeLabel}}
                </Text>
              </div>

              {{! What happens next }}
              <div class="p-6">
                <Heading
                  @level={{3}}
                  class="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4"
                >
                  {{t "observability.wizard.complete.whats_next"}}
                </Heading>
                <div class="space-y-4 mb-8">
                  <div class="flex items-start gap-4">
                    <div
                      class="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 shrink-0"
                    >
                      <span class="text-sm font-bold text-emerald-700">{{1}}</span>
                    </div>
                    <div>
                      <Text class="font-medium text-gray-900">{{t
                          "observability.wizard.complete.step1_title"
                        }}</Text>
                      <Text class="text-sm text-gray-500">{{t
                          "observability.wizard.complete.step1_description"
                        }}</Text>
                    </div>
                  </div>
                  <div class="flex items-start gap-4">
                    <div
                      class="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 shrink-0"
                    >
                      <span class="text-sm font-bold text-emerald-700">{{2}}</span>
                    </div>
                    <div>
                      <Text class="font-medium text-gray-900">{{t
                          "observability.wizard.complete.step2_title"
                        }}</Text>
                      <Text class="text-sm text-gray-500">{{t
                          "observability.wizard.complete.step2_description"
                        }}</Text>
                    </div>
                  </div>
                  <div class="flex items-start gap-4">
                    <div
                      class="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 shrink-0"
                    >
                      <span class="text-sm font-bold text-emerald-700">{{3}}</span>
                    </div>
                    <div>
                      <Text class="font-medium text-gray-900">{{t
                          "observability.wizard.complete.step3_title"
                        }}</Text>
                      <Text class="text-sm text-gray-500">{{t
                          "observability.wizard.complete.step3_description"
                        }}</Text>
                    </div>
                  </div>
                </div>

                {{! Processing status (real-time) }}
                {{#if this.observableDocument}}
                  {{#if this.upload}}
                    <div class="rounded-xl border border-gray-200 bg-gray-50 p-4 mb-6">
                      <UploadProgress
                        @observableDocument={{this.observableDocument}}
                        @source={{this.productSource}}
                        @upload={{this.upload}}
                        @uploadError={{this.uploadError}}
                        @onRetry={{this.resetWizard}}
                        @timedOut={{this.timedOut}}
                        @providers={{this.providers}}
                        @returnTo={{this.returnTo}}
                      />
                    </div>
                  {{/if}}
                {{/if}}

                {{! Action buttons }}
                <div class="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-100">
                  {{#if this.returnTo}}
                    <LinkTo
                      @route={{this.returnTo}}
                      class="flex-1 inline-flex items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
                    >
                      {{t "observability.wizard.complete.view_dashboard"}}
                      <PhArrowRight @size={{16}} />
                    </LinkTo>
                  {{/if}}
                  <Button
                    @intent="primary"
                    @appearance="outlined"
                    class="*:justify-center flex-1"
                    {{on "click" this.resetWizard}}
                  >
                    {{t "observability.wizard.complete.upload_another"}}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        {{/if}}
      </VStack>
    {{else}}
      <VStack>
        <Header>
          <LinkTo @route={{this.backRoute}} class="flex items-center mb-4 text-sm">
            <PhCaretLeft @size="16" />
            {{t "common.back"}}
          </LinkTo>
          <Heading @level={{1}}>{{t
              "observability.uploads.title"
              source=this.productSource
            }}</Heading>
        </Header>
        <p>{{t "observability.uploads.subtitle" source=this.productSource}}</p>
        <HStack>
          <Section>
            <:header>
              {{t "observability.uploads.form_section_title" source=this.productSource}}
            </:header>
            <:body>
              <form {{on "submit" this.handleSubmit}}>
                {{#if this.upload}}
                  {{! min height to match file uploader }}
                  <Section class="min-h-44">
                    <:header>
                      {{t "observability.uploads.form.document.selected"}}
                    </:header>
                    <:body>
                      <div class="flex items-center px-4 pt-4 text-tio-gray-600">
                        <PhFile @size="24" class="mr-2" />
                        <span>{{this.upload.file.name}}</span>
                        <button class="ml-auto" type="button" {{on "click" this.removeFile}}>
                          <PhXCircle @size="24" />
                        </button>
                      </div>
                    </:body>
                  </Section>
                {{else}}
                  <h3>{{t "observability.uploads.form.document.label"}}</h3>
                  <FileUploader
                    @name="observable-assets"
                    @onFileAdded={{this.handleFileAdded}}
                    @useDropzone={{true}}
                  >
                    <button type="submit">
                      <PhFile @size="24" class="mr-2" />
                      {{t "observability.uploads.form.document.input"}}
                    </button>
                  </FileUploader>
                {{/if}}
                <Button
                  @intent="primary"
                  type="submit"
                  @isDisabled={{not this.upload}}
                  class="mt-4 *:justify-center w-full"
                >
                  {{t "observability.uploads.form.submit"}}
                </Button>
              </form>
            </:body>
          </Section>
          <Section>
            <:header>
              {{t
                "observability.uploads.instructions_section_title"
                providers=this.providers.length
              }}
            </:header>
            <:body>
              <VStack @collapsed={{true}}>
                <Section>
                  <:header>
                    <span class="block text-fuscia-300 text-sm mb-2">
                      {{t "observability.uploads.best_prefix"}}
                    </span>
                    {{t (providerPromptTranslation this.preferredProvider)}}
                  </:header>
                  <:body>
                    <ActionableTranslation
                      @t={{providerCtaTranslation this.preferredProvider}}
                      @actions={{hash
                        openInstructions=(hash
                          t="click_here" onClick=(fn this.openInstructions this.preferredProvider)
                        )
                      }}
                    />
                  </:body>
                </Section>
                {{#if this.alternateProvider}}
                  <Section>
                    <:header>
                      <span class="block text-gray-600 text-sm mb-2">
                        {{t "observability.uploads.optional_prefix"}}
                      </span>
                      {{t (providerPromptTranslation this.alternateProvider)}}
                    </:header>
                    <:body>
                      <ActionableTranslation
                        @t={{providerCtaTranslation this.alternateProvider}}
                        @actions={{hash
                          openInstructions=(hash
                            t="click_here" onClick=(fn this.openInstructions this.alternateProvider)
                          )
                        }}
                      />
                    </:body>
                  </Section>
                {{/if}}
              </VStack>
            </:body>
          </Section>
        </HStack>
      </VStack>
    {{/if}}
    <Modal
      @isOpen={{or this.uploading this.processing}}
      @closeOnOutsideClick={{false}}
      @closeOnEscapeKey={{false}}
      @allowCloseButton={{false}}
      @onClose={{this.noop}}
      @size="xl"
    >
      <:header>{{t "observability.uploads.progress.header"}}</:header>
      <:body>
        {{#if this.upload}}
          <UploadProgress
            @observableDocument={{this.observableDocument}}
            @source={{this.productSource}}
            @upload={{this.upload}}
            @uploadError={{this.uploadError}}
            @onRetry={{this.reset}}
            @timedOut={{this.timedOut}}
            @providers={{this.providers}}
            @returnTo={{this.returnTo}}
          />
        {{/if}}
      </:body>
    </Modal>
    <Drawer
      @isOpen={{this.instructionsOpen}}
      @onClose={{this.closeInstructions}}
      @size="lg"
      @placement="right"
      @closeOnOutsideClick={{false}}
      @closeOnEscapeKey={{true}}
    >
      <:header>
        {{t (drawerHeaderTranslation this.instructionsType)}}
      </:header>
      <:body>
        <div class="p-4">
          {{#if this.needsServicer}}
            <span class="block font-semibold">
              {{t "observability.uploads.financial_institution.label"}}
            </span>
            <span class="block text-sm leading-none pt-4">
              {{t
                "observability.uploads.financial_institution.help"
                documentType=(t (documentTypeNameTranslation this.instructionsType))
              }}
            </span>
            <ReSelect
              @items={{this.servicerOptions}}
              @clearable={{true}}
              @selected={{this.selectedServicerKey}}
              @onChange={{this.setFinancialInstitution}}
            />
            {{#if this.financialInstitution}}
              <article class="prose prose-sm dark:prose-invert max-w-none">
                {{t
                  (drawerInstructionsTranslation this.instructionsType)
                  servicer=(getInstitutionMatch this.financialInstitution.name)
                  htmlSafe=true
                }}
              </article>
            {{/if}}
          {{else}}
            <article class="prose prose-sm dark:prose-invert max-w-none">
              {{t
                (drawerInstructionsTranslation this.instructionsType)
                servicer=(getInstitutionMatch this.financialInstitution.name)
                htmlSafe=true
              }}
            </article>
          {{/if}}
        </div>
      </:body>
    </Drawer>
  </template>
}
